import type { ReviewType } from '@wineryxplorer/shared';

// Star display component (read-only)
export const StarDisplay = ({ value, size = 'sm' }: { value: number; size?: 'sm' | 'md' }) => {
  const sizeClass = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`${sizeClass} ${star <= value ? 'text-yellow-400' : 'text-gray-300'}`}
          fill="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
      ))}
      <span className="ml-1 text-sm text-gray-600">{value.toFixed(1)}</span>
    </div>
  );
};

// Rating breakdown for venue/event/experience reviews
export const RatingBreakdown = ({
  ratings,
  reviewType,
}: {
  ratings: Record<string, number | undefined>;
  reviewType: ReviewType;
}) => {
  if (reviewType === 'wine') {
    return null; // Wine reviews have different display
  }

  const labels: Record<string, string> = {
    serviceAndStaff: 'Service & Staff',
    facility: 'Facility',
    location: 'Location',
    wines: 'Wines',
    food: 'Food',
    value: 'Value',
  };

  const ratingKeys =
    reviewType === 'venue'
      ? ['serviceAndStaff', 'facility', 'wines', 'value']
      : ['serviceAndStaff', 'location', 'wines', 'food', 'value'];

  return (
    <div className="flex flex-col gap-1 text-sm">
      {ratingKeys.map((key) => {
        const value = ratings[key];
        if (value === undefined) return null;
        return (
          <div key={key} className="flex items-center gap-2">
            <span className="text-gray-600 w-28">{labels[key]}:</span>
            <StarDisplay value={value} size="sm" />
          </div>
        );
      })}
    </div>
  );
};

// Wine score display with Parker scale
export const WineScoreDisplay = ({ score, valueForMoney }: { score: number; valueForMoney: number }) => {
  const getScoreCategory = (s: number): { label: string; color: string } => {
    if (s >= 96) return { label: 'Extraordinary', color: 'text-secondary-800 bg-secondary-50 border border-secondary-200' };
    if (s >= 90) return { label: 'Outstanding',   color: 'text-primary-800 bg-primary-50 border border-primary-200' };
    if (s >= 80) return { label: 'Very Good',     color: 'text-gray-800 bg-gray-100 border border-gray-200' };
    if (s >= 70) return { label: 'Average',       color: 'text-gray-700 bg-gray-100 border border-gray-200' };
    if (s >= 60) return { label: 'Below Average', color: 'text-amber-900 bg-amber-50 border border-amber-200' };
    return           { label: 'Unacceptable',     color: 'text-error bg-gray-100 border border-gray-200' };
  };

  const category = getScoreCategory(score);

  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-2">
        <span className="text-2xl font-bold text-gray-900">{score}</span>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${category.color}`}>
          {category.label}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-gray-600 text-sm">Value:</span>
        <StarDisplay value={valueForMoney} size="sm" />
      </div>
    </div>
  );
};
